<script>
import DatePicker from 'vue-datepicker-next';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Portuguese (Brazil) calendar strings, mirrored from
// node_modules/vue-datepicker-next/locale/pt-br.es.js's data. We inline the data
// rather than side-effect-importing that module directly: it self-registers via
// `DatePicker.locale('pt-br', lang)` at import time, which throws
// "DatePicker.locale is not a function" under this app's Vite bundling (the
// locale module's own `import DatePicker from 'vue-datepicker-next'` resolves to
// a different instance than the one used here) and crashes the whole dashboard
// on boot. The component reads locale data from `lang.formatLocale.*` (confirmed
// in the compiled bundle), not from a flat `days` key — the pre-existing English
// override below used `days`, which the component never reads, so it was a
// silent no-op; fixed here to the shape it actually consumes.
const PT_BR_LANG = {
  formatLocale: {
    months: [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ],
    monthsShort: [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ],
    weekdays: [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ],
    weekdaysShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    weekdaysMin: ['Do', '2ª', '3ª', '4ª', '5ª', '6ª', 'Sá'],
    firstDayOfWeek: 0,
    firstWeekContainsDate: 6,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
};

const EN_LANG = {
  formatLocale: {
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthsShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    weekdays: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    weekdaysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    firstDayOfWeek: 0,
    firstWeekContainsDate: 1,
  },
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
};

export default {
  components: {
    DatePicker,
    NextButton,
  },
  props: {
    messageContent: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'schedule'],
  data() {
    return {
      scheduledAt: null,
    };
  },
  computed: {
    isConfirmDisabled() {
      return !this.scheduledAt || !this.messageContent?.trim();
    },
    lang() {
      return this.$root.$i18n.locale === 'pt_BR' ? PT_BR_LANG : EN_LANG;
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    confirmSchedule() {
      if (this.isConfirmDisabled) return;
      this.$emit('schedule', this.scheduledAt);
    },
    disabledDate(date) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return date < yesterday;
    },
    disabledTime(date) {
      const now = new Date();
      return date < now;
    },
  },
};
</script>

<template>
  <div class="flex flex-col">
    <woot-modal-header
      :header-title="$t('CONVERSATION.SCHEDULE_MESSAGE.TITLE')"
      :header-content="$t('CONVERSATION.SCHEDULE_MESSAGE.DESC')"
    />
    <form
      class="modal-content w-full pt-2 px-5 pb-6"
      @submit.prevent="confirmSchedule"
    >
      <p v-if="!messageContent?.trim()" class="mb-3 text-sm text-n-ruby-11">
        {{ $t('CONVERSATION.SCHEDULE_MESSAGE.EMPTY_MESSAGE_WARNING') }}
      </p>
      <DatePicker
        v-model:value="scheduledAt"
        type="datetime"
        inline
        input-class="mx-input"
        format="YYYY-MM-DD HH:mm"
        :show-second="false"
        :minute-step="5"
        :lang="lang"
        :disabled-date="disabledDate"
        :disabled-time="disabledTime"
      />
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('CONVERSATION.SCHEDULE_MESSAGE.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          icon="i-lucide-clock"
          :disabled="isConfirmDisabled"
          :label="$t('CONVERSATION.SCHEDULE_MESSAGE.CONFIRM')"
        />
      </div>
    </form>
  </div>
</template>
